import Vue from 'vue'
import Vuetify from '../plugins/vuetify'
import store from '@/store'

export function leadingSlash (str) {
  return str.startsWith('/') ? str : '/' + str
}

export function trailingSlash (str) {
  return str.endsWith('/') ? str : str + '/'
}

export const wait = timeout => {
  return new Promise(resolve => setTimeout(resolve, timeout))
}

export function isMobile () {
  return Vuetify.framework.breakpoint.name === 'xs' || Vuetify.framework.breakpoint.name === 'sm'
}

export const TABLE_ENTITY_NAME_TRUNCATE_LIMIT = 30

export function truncateTableEntityName (text, limit = TABLE_ENTITY_NAME_TRUNCATE_LIMIT) {
  if (!text) {
    return ''
  }
  return text.length > limit ? `${text.slice(0, limit - 3)}...` : text
}

export function formatRequestCustomerTableLabel (item = {}, options = {}) {
  const {
    idKey = 'id',
    customerNameKey = 'customerName',
    limit = TABLE_ENTITY_NAME_TRUNCATE_LIMIT,
  } = options

  const id = item?.[idKey]
  const customerName = item?.[customerNameKey] || ''
  return truncateTableEntityName(`#${id} | ${customerName}`, limit)
}

export function getMonthDayOptions (month, year) {
  if (!month || !year) {
    return []
  }

  const lastDay = new Date(Number(year), Number(month), 0).getDate()
  return Array.from({ length: lastDay }, (_, index) => ({
    value: index + 1,
    text: String(index + 1).padStart(2, '0'),
  }))
}

function formatDateForFilter (date) {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export function buildMonthDateRange ({ month, year, startDay, endDay } = {}) {
  if (!month || !year) {
    return { startDate: null, endDate: null }
  }

  const dayOptions = getMonthDayOptions(month, year)
  const maxDay = dayOptions.length
  const normalizedStartDay = Number(startDay) || null
  const normalizedEndDay = Number(endDay) || null

  if (
    !normalizedStartDay ||
    !normalizedEndDay ||
    normalizedStartDay > normalizedEndDay ||
    normalizedStartDay < 1 ||
    normalizedEndDay > maxDay
  ) {
    const start = new Date(Number(year), Number(month) - 1, 1)
    const end = new Date(Number(year), Number(month), 0)

    return {
      startDate: formatDateForFilter(start),
      endDate: formatDateForFilter(end),
    }
  }

  const start = new Date(Number(year), Number(month) - 1, normalizedStartDay)
  const end = new Date(Number(year), Number(month) - 1, normalizedEndDay)

  return {
    startDate: formatDateForFilter(start),
    endDate: formatDateForFilter(end),
  }
}

function normalizeToastMessages (input, fallbackMessage) {
  if (input == null || input === '') {
    return [fallbackMessage]
  }

  if (Array.isArray(input)) {
    return input
      .map(item => {
        if (typeof item === 'string') {
          return item
        }
        if (item?.message) {
          return item.message
        }
        return null
      })
      .filter(Boolean)
  }

  if (typeof input === 'string') {
    return [input]
  }

  if (input?.response?.data) {
    const { data } = input.response

    if (typeof data === 'string') {
      return [data]
    }
    if (Array.isArray(data)) {
      return normalizeToastMessages(data, fallbackMessage)
    }
    if (typeof data?.message === 'string') {
      return [data.message]
    }
  }

  if (typeof input?.message === 'string') {
    return [input.message]
  }

  return [fallbackMessage]
}

function parseTextPayload (text) {
  if (!text) {
    return text
  }

  try {
    return JSON.parse(text)
  } catch (e) {
    return text
  }
}

async function normalizeToastMessagesAsync (input, fallbackMessage) {
  if (!input?.response?.data) {
    return normalizeToastMessages(input, fallbackMessage)
  }

  const { data } = input.response
  let normalizedData = data

  if (data instanceof ArrayBuffer) {
    normalizedData = parseTextPayload(new TextDecoder('utf-8').decode(data))
  } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
    normalizedData = parseTextPayload(await data.text())
  }

  return normalizeToastMessages({
    ...input,
    response: {
      ...input.response,
      data: normalizedData,
    },
  }, fallbackMessage)
}

function toastByType (type, input, fallbackMessage) {
  const messages = normalizeToastMessages(input, fallbackMessage)
  const options = Vue.$toastDefaults?.[type] || {}

  messages.forEach(message => {
    Vue.$toast[type](message, options)
  })
}

async function toastByTypeAsync (type, input, fallbackMessage) {
  const messages = await normalizeToastMessagesAsync(input, fallbackMessage)
  const options = Vue.$toastDefaults?.[type] || {}

  messages.forEach(message => {
    Vue.$toast[type](message, options)
  })
}

export function showSuccess (e) {
  toastByType('success', e, 'Operação realizada com sucesso')
}

export function showError (e) {
  if (e?.__maintenanceRedirect) {
    return
  }

  toastByTypeAsync('error', e, 'Erro inesperado')
    .catch(() => toastByType('error', e, 'Erro inesperado'))
}

export function parseNumber (number, decimalpoint = ',') {
  if (!number) {
    return 0
  }

  const p = number.split(decimalpoint)
  for (let i = 0; i < p.length; i++) {
    p[i] = p[i].replace(/\D/g, '')
  }
  return parseFloat(p.join('.'))
}

export async function playSound () {
  new Audio(require('@/assets/notification_sound.mp3')).play()
    .catch(e => {
      console.log(e)
    })
}

export function allow (id, items) {
  return items.find(i => String(i) === String(id)) != null
}

export function getQueryFromFilter (filter = {}) {
  const {
    search = '',
    page = 1,
    itemsPerPage = 20,
    sortBy = [],
    sortDesc = [],
    ...rest // todos os outros filtros dinâmicos
  } = filter

  const params = {
    search,
    page,
    limit: itemsPerPage,
    ...rest,

    companyId: store.get('authStore@currentCompany')?.id,
  }

  // Ordenação
  if (sortBy.length > 0) {
    params.order = sortBy[0]
  }
  if (sortDesc.length > 0) {
    params.sort = sortDesc[0] ? 'desc' : 'asc'
  }

  // Remove chaves com valor vazio, null ou undefined
  Object.keys(params).forEach(
    (key) => (params[key] === '' || params[key] == null) && delete params[key],
  )

  return new URLSearchParams(params).toString()
}

function isMeaningfulFilterValue (value) {
  if (Array.isArray(value)) {
    return value.some(item => isMeaningfulFilterValue(item))
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  if (typeof value === 'boolean') {
    return value === true
  }

  return value !== null && value !== undefined
}

export function hasActiveFilterValues (filter = {}, keys = []) {
  return keys.some(key => isMeaningfulFilterValue(filter?.[key]))
}

export function getStringFromFilter (filter) {
  let str = ''

  if (filter.startDate && filter.endDate) {
    str += `Data: ${filter.startDate} a ${filter.endDate}; `
  }

  if (filter.userId) {
    str += `Usuário: ${store.get('userStore/getById', filter.userId).name}; `
  }

  if (filter.userType) {
    switch (filter.userType) {
      case 1:
        str += 'Tipo: Administrador; '
        break
      case 2:
        str += 'Tipo: Supervisor; '
        break
      case 3:
        str += 'Tipo: Vendedor; '
        break
    }
  }

  if (filter.categoryId) {
    str += `Categoria: ${store.get('categoryStore/getById', filter.categoryId).name}; `
  }

  if (filter.brandId) {
    str += `Marca: ${store.get('brandStore/getById', filter.brandId).name}; `
  }

  if (filter.unityId) {
    str += `Unidade: ${store.get('unityStore/getById', filter.unityId).name}; `
  }

  if (filter.productType) {
    str += `Tipo: ${filter.productType}; `
  }

  if (filter.userId) {
    str += `Usuário: ${store.get('userStore/getById', filter.userId).name}; `
  }

  if (filter.customerId) {
    const customerName = store.get('customerStore/getById', filter.customerId).name
    str += `Cliente: ${truncateTableEntityName(customerName)}; `
  }

  if (filter.productId) {
    str += `Produto: ${store.get('productStore/getById', filter.productId).name}; `
  }

  if (filter.paymentMethodId) {
    str += `Metodo de pagamento: ${store.get('paymentMethodStore/getById', filter.paymentMethodId)}; `
  }

  if (filter.accountId) {
    str += `Conta: ${store.get('accountStore/getById', filter.accountId).bank}; `
  }

  if (filter.providerId) {
    const providerName = store.get('providerStore/getById', filter.providerId).name
    str += `Fornecedor: ${truncateTableEntityName(providerName)}; `
  }

  if (filter.classificationId) {
    str += `Classificação: ${store.get('classificationStore/getById', filter.classificationId).name}; `
  }

  if (filter.status) {
    str += `Status: ${filter.status}; `
  }

  if (filter.onlyPayed) {
    str += 'Somente Pedidos Pagos; '
  } else if (filter.onlyNotPayed) {
    str += 'Somente Pedidos Não Pagos; '
  }

  if (filter.onlyActive) {
    str += 'Somente ativos; '
  } else if (filter.onlyInactive) {
    str += 'Somente inativos; '
  }

  if (filter.order) {
    switch (filter.order) {
      case 'id':
        str += 'Ordem: Código'
        break
      case 'name':
        str += 'Ordem: Nome'
        break
      case 'userId':
        str += 'Ordem: Usuário'
        break
      case 'value':
        str += 'Ordem: Valor'
        break
      case 'createdAt':
        str += 'Ordem: Data de criação'
        break
      case 'description':
        str += 'Ordem: Descrição'
        break
      case 'type':
        str += 'Ordem: Tipo'
        break
      case 'date':
        str += 'Ordem: Data'
        break
    }
  }

  return str
}

export function addHeadersAndFooters (data) {
  const { doc, title, orientation = 'landscape' } = data
  const { includeLogo, includeDateTime } = store.get('authStore@currentCompany')
  const pageCount = doc.internal.getNumberOfPages()

  doc.setFont('helvetica', 'italic')
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)

    // logo
    const imageSrc = store.get('authStore@currentCompany').logoSmall
    if (includeLogo && !!imageSrc) {
      const img = new Image()
      img.src = imageSrc
      img.style = 'border-radius: 50%;'

      doc.addImage(img, 'png', 20, 15, 30, 23)
    }

    // texto
    doc.setFontSize(8)
    doc.text(store.get('authStore@currentCompany').name, 55, 30)
    doc.setFontSize(12)
    doc.text(
      title,
      doc.internal.pageSize.width / 2 -
      (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) / 2,
      20,
    )
    doc.setFontSize(8)
    const date = new Date()
    doc.text(
      String(i) + ' de ' + String(pageCount),
      doc.internal.pageSize.width - 100,
      30,
    )
    doc.line(20, 40, doc.internal.pageSize.width - 20, 40)

    let height = 585
    if (orientation !== 'landscape') {
      height = 830
    }

    // rodapé
    doc.text(store.get('authStore@currentUser').name, 40, height)
    if (includeDateTime) {
      doc.text(
        date.toLocaleDateString() + ' - ' + date.toLocaleTimeString(),
        doc.internal.pageSize.width - 120,
        height,
      )
    }
  }

  return doc
}

export function brToIso (str) {
  const [date, time] = str.split(' ')
  const [d, m, y] = date.split('/')

  return `${y}-${m}-${d}T${time || '00:00'}`
}

export function removeFormat (value) {
  if (!value) {
    return ''
  }
  return String(value).replace(/\D/g, '')
}

export async function getCep (cep) {
  const cepLimpo = String(cep).replace(/\D/g, '')

  if (cepLimpo.length !== 8) {
    throw new Error('CEP inválido')
  }

  const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`)

  if (!response.ok) {
    throw new Error('Erro ao consultar o CEP')
  }

  const data = await response.json()

  if (data?.erro) {
    throw new Error('CEP não encontrado')
  }

  return data
}
